'use client'

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from '@clerk/nextjs'
import api from '@/lib/api'

interface Folder {
    id: number
    name: string
    user_id: number
    created_at: string
}

interface FolderContextType {
    folders: Folder[]
    loading: boolean
    refreshFolders: () => Promise<void>
}

const FolderContext = createContext<FolderContextType | undefined>(undefined)

export function FolderProvider({ children }: { children: React.ReactNode }) {
    const { isLoaded, userId, getToken } = useAuth()
    const [folders, setFolders] = useState<Folder[]>([])
    const [loading, setLoading] = useState(false)
    const [hasFetched, setHasFetched] = useState(false)

    const refreshFolders = useCallback(async () => {
        if (!isLoaded || !userId) return

        setLoading(true)
        try {
            const token = await getToken()
            const response = await api.get('/api/folders', {
                headers: { Authorization: `Bearer ${token}` }
            })
            setFolders(response.data)
            setHasFetched(true)
        } catch (err) {
            console.error('Failed to fetch folders:', err)
        } finally {
            setLoading(false)
        }
    }, [isLoaded, userId, getToken])

    useEffect(() => {
        if (isLoaded && userId && !hasFetched) {
            refreshFolders()
        }
    }, [isLoaded, userId, hasFetched, refreshFolders])

    // Reset hasFetched when user logs out
    useEffect(() => {
        if (!userId) {
            setFolders([])
            setHasFetched(false)
        }
    }, [userId])

    return (
        <FolderContext.Provider value={{ folders, loading, refreshFolders }}>
            {children}
        </FolderContext.Provider>
    )
}

export function useFolders() {
    const context = useContext(FolderContext)
    if (context === undefined) {
        throw new Error('useFolders must be used within a FolderProvider')
    }
    return context
}
